import React, { useCallback, useMemo } from "react";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { Dropdown } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import { removeSimpleConditionForFields } from "@app/util/filters/utils";

import getResourceContentTypeFilter, {
  getResourceContentTypeFromFilter,
} from "./getResourceContentTypeFilter";
import ResourceContentType, { getResourceContentTypeName } from "./ResourceContentType";
import styles from "./ResourceTypeFilter.module.scss";

const CLEAR_OPTION_VALUE = "clear";

interface ResourceTypeFilterProps {
  contentTypes?: ResourceContentType[];
  filter: FilterModel;
  onChange: (filter: FilterModel) => void;
}

const ResourceTypeFilter = ({ contentTypes, filter, onChange }: ResourceTypeFilterProps) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const resourceTypeItems: MenuProps["items"] = useMemo(
    () => [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage({
          id: "component.resources.type.filter.clear",
          defaultMessage: "Clear",
          description: "Clear option in filter component",
        }),
      },
      ...(contentTypes ?? Object.values(ResourceContentType)).map((type) => ({
        key: type,
        label: getResourceContentTypeName(intl, type),
      })),
    ],
    [contentTypes, intl]
  );

  const selectedResourceType = useMemo(
    () => (filter ? getResourceContentTypeFromFilter(filter) : undefined),
    [filter]
  );

  const handleMenuClick = useCallback(
    (item: MenuInfo) => {
      const key = item.key as ResourceContentType | typeof CLEAR_OPTION_VALUE;
      onChange(
        key === CLEAR_OPTION_VALUE
          ? removeSimpleConditionForFields(filter, ResourceFieldName.TYPE, ResourceFieldName.URL)
          : getResourceContentTypeFilter(filter, key)
      );
      analytics.clicked([
        "Resource Type",
        key === "clear" ? "Clear" : getResourceContentTypeName(intl, key),
      ]);
    },
    [onChange, filter, analytics, intl]
  );
  return (
    <Dropdown
      menu={{
        items: resourceTypeItems,
        onClick: handleMenuClick,
        selectedKeys: selectedResourceType ? [selectedResourceType] : [],
      }}
      trigger={["click"]}
    >
      <RoundButton
        active={!!selectedResourceType}
        rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
      >
        {selectedResourceType
          ? getResourceContentTypeName(intl, selectedResourceType)
          : intl.formatMessage({
              id: "component.resources.type.filter.resourceType",
              defaultMessage: "Resource Type",
              description: "Resource Type filter",
            })}
      </RoundButton>
    </Dropdown>
  );
};

export default ResourceTypeFilter;
